import { useState, useEffect, useRef, forwardRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment-timezone';
import DatePicker from '../date-picker/DatePicker';
import TimePicker from '../time-picker/TimePicker';
import scrollToComponent from 'react-scroll-to-component';
import { setSelectedLessonTime } from '../../redux/user-selection/userSelection';
import { getAvailabilityForDay } from '../../redux/availability/availability';
import { appRoutesByName } from '../../containers/app/Routes';
import { createSelector } from 'reselect';
import './Availability.css';
import error from '../../images/error.svg';
import type { RootState } from '../../app/store'; // Adjust the path to your store file

export interface Props {
    geoareaId?: number | undefined;
    searchedDates?: Date[];
    availableTimes?: Record<string, any>;
    firstAvailableDay?: Date | undefined;
    availabilityLoading?: boolean;
    searchingDay?: boolean;
}

const selectAvailabilityData = createSelector(
    (state: RootState) => state.availability,
    (state: RootState) => state.userSelection.location?.geoareaId,
    (availability, geoareaId) => ({
        availableTimes: availability.firstAvailableDay ? availability.availableTimes : {},
        firstAvailableDay: availability.firstAvailableDay,
        availabilityLoading: availability.loading,
        geoareaId,
        searchedDates: availability.daysSearched || [],
        searchingDay: availability.searchingDay,
    })
);

const Availability = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const [selectedDate, setSelectedDate] = useState<Date | undefined>();
    const [timeSelected, setTimeSelected] = useState<Date | undefined>();
    const [continueEnabled, setContinueEnabled] = useState(false);
    const timePickerRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    
    const dispatch = useAppDispatch();

    const {
        geoareaId,
        searchedDates,
        availableTimes,
        firstAvailableDay,
        availabilityLoading,
        searchingDay,
    } = useAppSelector(selectAvailabilityData);

    useEffect(() => {
        if (selectedDate) {
            scrollToTimePicker();
        }
    }, [selectedDate]);

    const generateDates = (firstAvailableDay: moment.Moment) => {
        const today = moment();
        const sixMonths = moment(today).add(6, 'months');
        const datesArray: Date[] = [];
        const dayToShow = moment(firstAvailableDay);

        while (dayToShow <= sixMonths) {
            datesArray.push(dayToShow.toDate());
            dayToShow.add(1, 'day');
        }
        return datesArray;
    };

    const timeSelect = (time: Date) => {
        setTimeSelected(time);
        setContinueEnabled(true);
    };

    const dateSelect = (date: Date) => {
        if (date && geoareaId && !dateInArray(searchedDates, date)) {
            dispatch(getAvailabilityForDay({ geoareaId, date }));
        }
        setSelectedDate(date);
        scrollToTimePicker();
    };

    const dateInArray = (arr: Date[], date: Date) => {
        return arr.some((d) => moment(date).isSame(d, 'day'));
    };

    const scrollToTimePicker = () => {
        if (timePickerRef.current) {
            scrollToComponent(timePickerRef.current, { align: 'middle' });
        }
    };

    const onClick = () => {
        if (timeSelected) {
            dispatch(setSelectedLessonTime({ lessonTime: timeSelected }));
            navigate(appRoutesByName.details.path);
        } else {
            console.error('No time selected');
        }
    };

    const searchBtnClasses = classNames(
        'a-button',
        'a-button--primary',
        'a-button--full-width',
        { 'is-disabled': !continueEnabled }
    );

    const genDates = firstAvailableDay ? generateDates(moment(firstAvailableDay)) : [];
    const showTimePicker =
        selectedDate &&
        !searchingDay &&
        availableTimes[moment(selectedDate).format('DD-MM-YYYY')];
    const showNoTimes =
        selectedDate &&
        !searchingDay &&
        !availableTimes[moment(selectedDate).format('DD-MM-YYYY')];

    if (availabilityLoading) {
        return (
            <div data-testid="loading-indicator" className="a-loading-indicator-container centre" ref={ref}>
                <div className="a-loading-indicator">
                    <span className="a-loading-indicator__message">Avail Loading...</span>
                </div>
            </div>
        );
    } else if (genDates.length < 1) {
        return (
            <div className="availability-container" ref={ref} data-testid="availability-container">
                <h1 className="text__center header">No dates available</h1>
                <p className="text__center">Please select another pick up point to view available times</p>
            </div>
        );
    } else {
        return (
            <div className="availability-container" ref={ref} data-testid="availability-container">
                <h1 className="text__center header">Select date</h1>
                <p className="text__center">Select a date below to view available times</p>
                <DatePicker dates={genDates} onSelect={dateSelect} />
                {selectedDate && showTimePicker && (
                    <div className="timePicker-container" ref={timePickerRef} data-testid="timePicker-container">
                        <h1 className="text__center time_header">Select time</h1>
                        <p className="text__center">What time do you want your driving lesson to start at?</p>
                        <TimePicker
                            times={availableTimes[moment(selectedDate).format('DD-MM-YYYY')]}
                            selectedDate={selectedDate}
                            onSelect={timeSelect}
                        />
                    </div>
                )}
                {searchingDay && (
                    <div className="time-container__loading">
                        <div className="time_header__loading" />
                        <div className="time_text__loading" />
                        <div className="timePicker-container__loading">
                            <div className="hour-loading" />
                            <div className="minute-loading" />
                            <div className="segment-loading" />
                        </div>
                    </div>
                )}
                {selectedDate && showNoTimes && (
                    <div className="time-error">
                        <img alt="error" className="time-error_img" src={error} />
                        <div>
                            <h3 className="error-text">
                                <strong>There is no availability on this day</strong>
                            </h3>
                        </div>
                        <span className="error-text">Please choose an alternative day</span>
                    </div>
                )}
                <div className="m-form-row button-center">
                    <button className={searchBtnClasses} onClick={onClick} disabled={!continueEnabled}>
                        Select and Continue
                    </button>
                </div>
            </div>
        );
    }
});

export default Availability;